import React, { useCallback, useState } from 'react'
import { Dimensions, FlatList, Modal, ScrollView } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Icon, Stack, XStack } from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { KeyBoardWrapper } from '@zet/utils'
import { useAddCustomerState } from '@zet/zet-pro-quick-journey/hooks/useAddCustomer'
import { useForm } from 'react-hook-form'

import { Button, ScreenTitle } from '../../components'
import { B12, B14, S12 } from '../../components/typography'
import FormController from '../../form/FormController'
import {
  sendAnalyticsData,
  sendJfsCustomEvent,
} from '../../utils/analyticsEvents'
import { getJfsCustomerId } from '../../utils/jfsSession'
import { customerBasicDetailsValidationSchema } from '../../utils/validationSchemas'
import { customerBasicDetailsFormFields } from './formFields'
import { TERMS_AND_CONDITIONS, TermsRun } from './termsAndConditionsText'

// Weight in this design system comes from the font family, not font-weight:
// `font-bold` on a JioType text is overridden by the base `font-medium`, so
// bold runs have to use the JioType-Bold face via B12.
const renderRuns = (runs: TermsRun[]) =>
  runs.map((run, index) => {
    const RunText = run.bold ? B12 : S12
    return (
      <RunText key={index} className={run.italic ? 'italic' : ''}>
        {run.text}
      </RunText>
    )
  })

export const CustomerBasicDetails = () => {
  const { mutate, isLoading } = useAddCustomerState()
  const { customerId } = useAddCustomerStore()
  const [isChecked, setIsChecked] = useState(false)
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false)

  const {
    control,
    formState: { isValid },
    getValues,
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(customerBasicDetailsValidationSchema),
    defaultValues: {
      email: '',
      pinCode: '',
      annualIncome: '',
      modeOfIncome: 'Bank',
      employmentType: 'Salaried',
    },
  })

  const renderItem = useCallback(({ item }) => {
    return <FormController control={control} ocField={item} />
  }, [])

  const handleSubmit = () => {
    const { employmentType, pincode, pinCode, annualIncome, modeOfIncome } =
      getValues()
    const custId = getJfsCustomerId()
    sendAnalyticsData({
      screen: 'SHOW_BASIC_DETAILS_SCREEN_JFS',
      type: 'click',
    })
    sendJfsCustomEvent('enter_work_details', {
      customer_id: custId || customerId || '',
      pincode: pinCode || pincode,
      employment_type: employmentType,
      click_header: 'Tell us about your work',
      click_text: 'Continue',
    })
    const monthlyIncome = annualIncome.replace(/,/g, '')

    const requestBodyData = {
      employmentType,
      pinCode: pinCode || pincode,
      annualIncome: monthlyIncome,
      modeOfIncome,
      customerId: custId || customerId,
      screenName: 'SHOWN_BASIC_DETAILS_SCREEN_JFS',
    }
    mutate(requestBodyData, {
      onSuccess: (data) => {
        sendAnalyticsData({ screen: data?.action, type: 'screen' })
      },
    })
  }

  const onCheckboxPress = () => {
    setIsChecked(!isChecked)
  }

  return (
    <KeyBoardWrapper
      BottomSheet={
        <Stack>
          <XStack className="items-center px-4">
            <Icon
              type={isChecked ? 'CheckBoxTickFilled' : 'CheckBox'}
              color={isChecked ? '#5D00B5' : '#0A0A0A'}
              size={24}
              className="mr-2"
              onPress={onCheckboxPress}
            />
            <S12 className="select-none">
              Accept{' '}
              <Pressable onPress={() => setIsTermsModalVisible(true)}>
                <S12 className="text-primary-150">Terms and Conditions</S12>
              </Pressable>
            </S12>
          </XStack>
          <Button
            disabled={!isValid || isLoading || !isChecked}
            onPress={handleSubmit}
            title={'Continue'}
            loading={isLoading}
            loaderText="Verifying"
          />
        </Stack>
      }
    >
      <Stack className="bg-white px-3">
        <FlatList
          showsVerticalScrollIndicator={false}
          data={customerBasicDetailsFormFields}
          ListHeaderComponent={
            <ScreenTitle
              title={'Tell us about your work'}
              subTitle="Share your professional info to verify eligibility."
            />
          }
          renderItem={renderItem}
        />
      </Stack>

      <Modal
        visible={isTermsModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsTermsModalVisible(false)}
      >
        <Pressable
          className="flex-1 bg-black/50 justify-center px-4"
          onPress={() => setIsTermsModalVisible(false)}
        >
          <Pressable onPress={(e) => e.stopPropagation()}>
            <Stack className="bg-white rounded-2xl p-4 max-h-[85%] min-h-[200px]">
              <XStack className="items-center justify-between mb-3">
                <S12 className="font-semibold text-grays-100">
                  Terms and Conditions
                </S12>
                <Pressable onPress={() => setIsTermsModalVisible(false)}>
                  <Icon type="Close" size={24} color="#0A0A0A" />
                </Pressable>
              </XStack>
              <ScrollView
                showsVerticalScrollIndicator={true}
                style={{
                  maxHeight: Dimensions.get('window').height * 0.5,
                }}
                contentContainerStyle={{ paddingBottom: 8 }}
                nestedScrollEnabled={true}
              >
                {TERMS_AND_CONDITIONS.map((section) => (
                  <Stack key={section.number} className="mb-3">
                    <B14 className="text-grays-100 mb-1">
                      {section.number}. {section.title}
                    </B14>
                    {section.blocks.map((block, index) => {
                      const key = `${section.number}-${index}`
                      // The source document lays every block out in three
                      // columns of equal steps: headings and clause numbers in
                      // the first, all body text and list markers in the
                      // second, list item text in the third. Wrapped lines hang
                      // under the body, so each leading column is a fixed
                      // gutter cell of the same width rather than inline text.
                      return (
                        <XStack key={key} className="mb-1">
                          {block.kind === 'listItem' ? (
                            <>
                              <Stack className="w-8" />
                              <S12 className="text-grays-100 w-8">
                                {block.marker}
                              </S12>
                            </>
                          ) : block.number ? (
                            <S12 className="text-grays-100 w-8">
                              {block.number}
                            </S12>
                          ) : (
                            <Stack className="w-8" />
                          )}
                          <S12 className="text-grays-100 flex-1">
                            {renderRuns(block.runs)}
                          </S12>
                        </XStack>
                      )
                    })}
                  </Stack>
                ))}
              </ScrollView>
            </Stack>
          </Pressable>
        </Pressable>
      </Modal>
    </KeyBoardWrapper>
  )
}
